import React, { useState } from 'react';
import { ArrowUpDownIcon, MapPinIcon } from 'lucide-react';
import { BranchData } from '../../utils/dashboardData';
interface BranchPerformanceProps {
  data: BranchData[];
}
type SortField = 'name' | 'transactions' | 'salesValue' | 'avgTicket';
type SortOrder = 'asc' | 'desc';
const BranchPerformance: React.FC<BranchPerformanceProps> = ({
  data
}) => {
  const [sortField, setSortField] = useState<SortField>('salesValue');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };
  const sortedData = [...data].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });
  const maxSales = Math.max(...data.map(b => b.salesValue));
  return <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-0 h-full flex flex-col overflow-hidden" data-id="element-61">
      <div className="p-6 border-b border-slate-100" data-id="element-62">
        <h2 className="text-lg font-bold text-slate-900" data-id="element-63">Store Performance</h2>
        <p className="text-sm text-slate-500 font-medium mt-1" data-id="element-64">
          Sales metrics across your locations
        </p>
      </div>

      <div className="overflow-x-auto flex-1" data-id="element-65">
        <table className="w-full text-sm text-left" data-id="element-66">
          <thead className="text-xs text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-slate-200 sticky top-0 z-10" data-id="element-67">
            <tr data-id="element-68">
              <th className="px-6 py-4 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('name')} data-id="element-69">
                <div className="flex items-center" data-id="element-70">
                  Branch{' '}
                  <ArrowUpDownIcon className="h-3.5 w-3.5 ml-1.5 text-slate-400" data-id="element-71" />
                </div>
              </th>
              <th className="px-6 py-4 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('transactions')} data-id="element-72">
                <div className="flex items-center justify-end" data-id="element-73">
                  Transactions{' '}
                  <ArrowUpDownIcon className="h-3.5 w-3.5 ml-1.5 text-slate-400" data-id="element-74" />
                </div>
              </th>
              <th className="px-6 py-4 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('salesValue')} data-id="element-75">
                <div className="flex items-center justify-end" data-id="element-76">
                  Sales Value{' '}
                  <ArrowUpDownIcon className="h-3.5 w-3.5 ml-1.5 text-slate-400" data-id="element-77" />
                </div>
              </th>
              <th className="px-6 py-4 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('avgTicket')} data-id="element-78">
                <div className="flex items-center justify-end" data-id="element-79">
                  Avg Ticket{' '}
                  <ArrowUpDownIcon className="h-3.5 w-3.5 ml-1.5 text-slate-400" data-id="element-80" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-81">
            {sortedData.map((branch, index) => {
            const isTop = index === 0 && sortField === 'salesValue' && sortOrder === 'desc';
            return <tr key={branch.id} className={`even:bg-slate-50/50 hover:bg-slate-50 transition-colors ${isTop ? 'bg-indigo-50/30 even:bg-indigo-50/30' : ''}`} data-id="element-82">
                  <td className="px-6 py-4" data-id="element-83">
                    <div className="flex items-center" data-id="element-84">
                      <div className={`w-1 h-10 mr-4 rounded-full ${isTop ? 'bg-indigo-500' : 'bg-transparent'}`} data-id="element-85"></div>
                      <div className={`p-2 rounded-lg mr-3 ${isTop ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`} data-id="element-86">
                        <MapPinIcon className="h-4 w-4" data-id="element-87" />
                      </div>
                      <div data-id="element-88">
                        <p className={`font-bold ${isTop ? 'text-indigo-900' : 'text-slate-900'}`} data-id="element-89">
                          {branch.name}
                        </p>
                        <p className="text-xs font-medium text-slate-500 mt-0.5" data-id="element-90">
                          {branch.id}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-slate-700" data-id="element-91">
                    {branch.transactions.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-92">
                    <div className="flex flex-col items-end" data-id="element-93">
                      <span className="font-bold text-slate-900" data-id="element-94">
                        ₨{branch.salesValue.toLocaleString()}
                      </span>
                      <div className="w-24 h-1.5 bg-slate-100 rounded-full mt-1.5 overflow-hidden" data-id="element-95">
                        <div className={`h-full rounded-full ${isTop ? 'bg-gradient-to-r from-indigo-500 to-violet-500' : 'bg-slate-300'}`} style={{
                      width: `${branch.salesValue / maxSales * 100}%`
                    }} data-id="element-96"></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-slate-600" data-id="element-97">
                    ₨{branch.avgTicket.toLocaleString()}
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </div>;
};
export default BranchPerformance;